import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProductCard from '../components/ProductCard';
import { useLanguage } from '../context/LanguageContext';
import { fetchCategories, fetchProducts } from '../services/api';

const CollectionsPage = () => {
  const { language, t } = useLanguage();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = searchParams.get('category') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialCategory);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [categoriesData, productsData] = await Promise.all([
          fetchCategories(),
          fetchProducts(activeCategory === 'all' ? null : activeCategory)
        ]);
        setCategories(categoriesData);
        setProducts(productsData);
      } catch (error) {
        console.error('Failed to load data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [activeCategory]);

  const handleCategoryChange = (categoryId) => {
    setActiveCategory(categoryId);
    if (categoryId === 'all') {
      setSearchParams({});
    } else {
      setSearchParams({ category: categoryId });
    }
  };

  return (
    <div className="min-h-screen bg-[#fffef2]">
      <Header />
      <main>
        {/* Page Header */}
        <section className="pt-32 pb-16 lg:pt-40 lg:pb-20 bg-[#f6f5e8]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 text-center">
            <h1 className="text-4xl md:text-5xl font-light text-[#333333] mb-4 tracking-wide">
              {t('collections.title')}
            </h1>
            <p className="text-[#666666] text-base font-light">
              {t('collections.subtitle')}
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="py-8 bg-[#fffef2] border-b border-[#ebeade]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => handleCategoryChange('all')}
                className={`px-6 py-3 text-sm font-medium tracking-wider uppercase transition-all duration-300 ${
                  activeCategory === 'all'
                    ? 'bg-[#333333] text-white'
                    : 'bg-transparent text-[#333333] border border-[#333333] hover:bg-[#333333] hover:text-white'
                }`}
              >
                {language === 'tr' ? 'Tümü' : 'All'}
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategoryChange(category.id)}
                  className={`px-6 py-3 text-sm font-medium tracking-wider uppercase transition-all duration-300 ${
                    activeCategory === category.id
                      ? 'bg-[#333333] text-white'
                      : 'bg-transparent text-[#333333] border border-[#333333] hover:bg-[#333333] hover:text-white'
                  }`}
                >
                  {category.name[language]}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Products Grid */}
        <section className="py-16 lg:py-24 bg-[#fffef2]">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-[#f6f5e8] aspect-square mb-4"></div>
                    <div className="h-4 bg-[#f6f5e8] mb-2 w-3/4"></div>
                    <div className="h-3 bg-[#f6f5e8] w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {products.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}

            {!loading && products.length === 0 && (
              <div className="text-center py-16">
                <p className="text-[#666666] text-base">
                  {language === 'tr'
                    ? 'Bu kategoride ürün bulunamadı.'
                    : 'No products found in this category.'}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default CollectionsPage;
